import { useMemo, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { icons, Edit, Database } from 'lucide-react';
import { HubView } from '@/components/HubView';
import type { HubTab, HubTagFilterOption } from '@/components/HubView';
import type { ApiContent } from '@/lib/api/types';
import { useSession } from '@/lib/auth-client';
import { useResourcePermissions } from '@/lib/hooks/useResourcePermissions';
import Link from 'next/link';
import { ObjectDataEditorDialog } from '@/components/admin/ObjectDataEditorDialog';

// --- Component registry ---
// Maps string keys from content.data.hub.tabs[].component to lazy-loaded React components.
// Add new hub-specific home components here.
const HUB_COMPONENT_REGISTRY: Record<string, React.ComponentType> = {
  KapitiCoastFBInfo: dynamic(() => import('@/components/KapitiCoastFBInfo').then(mod => mod.KapitiCoastFBInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  AucklandCentralFBInfo: dynamic(() => import('@/components/AucklandCentralFBInfo').then(mod => mod.AucklandCentralFBInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  PonsonbyFBInfo: dynamic(() => import('@/components/PonsonbyFBInfo').then(mod => mod.PonsonbyFBInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  StonefieldsFBInfo: dynamic(() => import('@/components/StonefieldsFBInfo').then(mod => mod.StonefieldsFBInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  NZProductToWorldInfo: dynamic(() => import('@/components/NZProductToWorldInfo').then(mod => mod.NZProductToWorldInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  StarGroupInfo: dynamic(() => import('@/components/StarGroupInfo').then(mod => mod.StarGroupInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  UoAActivationsInfo: dynamic(() => import('@/components/UoAActivationsInfo').then(mod => mod.UoAActivationsInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  CPGNewYorkInfo: dynamic(() => import('@/components/CPGNewYorkInfo').then(mod => mod.CPGNewYorkInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGVictoriaInfo: dynamic(() => import('@/components/FMCGVictoriaInfo').then(mod => mod.FMCGVictoriaInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGSingaporeInfo: dynamic(() => import('@/components/FMCGSingaporeInfo').then(mod => mod.FMCGSingaporeInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGSingaporeForNZBrandsInfo: dynamic(() => import('@/components/FMCGSingaporeForNZBrandsInfo').then(mod => mod.FMCGSingaporeForNZBrandsInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGIndiaForNZBrandsInfo: dynamic(() => import('@/components/FMCGIndiaForNZBrandsInfo').then(mod => mod.FMCGIndiaForNZBrandsInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGUnitedKingdomInfo: dynamic(() => import('@/components/FMCGUnitedKingdomInfo').then(mod => mod.FMCGUnitedKingdomInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  FMCGOfficeHoursInfo: dynamic(() => import('@/components/FMCGOfficeHoursInfo').then(mod => mod.FMCGOfficeHoursInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  NZFMCGClubInfo: dynamic(() => import('@/components/NZFMCGClubInfo').then(mod => mod.NZFMCGClubInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }),
  CollaborationsInfo: dynamic(() => import('@/components/CollaborationsInfo').then(mod => mod.CollaborationsInfo), {
    loading: () => <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  })
};

// --- Types for the hub data shape inside content.data ---

interface HubTabData {
  id: string;
  title?: string;
  view: string;
  tooltip?: string;
  description?: string;
  /** Lucide icon name to display in the tab button */
  icon?: string;
  /** Key into HUB_COMPONENT_REGISTRY for custom tab content */
  component?: string;
  /** When true, the tab is hidden and not rendered. */
  disabled?: boolean;
  /**
   * Per-tab context overrides – merged on top of hub-level context.
   *
   * For `view: "inspiration"` tabs (an embedded Inspiration Feed), the following
   * context keys are supported:
   * - `lockedFilters`: facet values that are always applied and cannot be toggled
   *   off or cleared by the user. Shape is a partial of the inspiration filter
   *   value, e.g. `{ "tactics": ["collaboration"] }`. The facet control stays
   *   visible so the user can still add other values on top (OR within a facet);
   *   locked values render as checked, non-removable options and chips.
   * - `visibleFilters`: array of facet keys to expose as controls (e.g.
   *   `["tactics", "channels"]`). Use this to hide a facet entirely; locked
   *   values still apply even if their facet is not listed.
   * - `showLockedFilters`: set `false` to hide locked filters from the UI while
   *   still applying them to the query. Defaults to `true` (locked values render
   *   as checked, non-removable options and chips).
   * - `limit`: page size for the feed.
   * - `showQuickFilters`: set `false` to hide the quick-filter pill row.
   *   Defaults to `true`.
   * - `quickFilters`: custom quick-filter presets that replace the default
   *   pills. Array of `{ id, label, filters }`, where `filters` maps facet keys
   *   to value arrays, e.g.
   *   `[{ "id": "in-store", "label": "In-store", "filters": { "channels": ["retail-in-store"] } }]`.
   *   Invalid entries are dropped.
   */
  context?: Record<string, any>;
}
interface HubOptions {
  /** Hide the hub title. Defaults to false. */
  hideTitle?: boolean;
  /** Override the displayed title (instead of content.title). */
  titleOverride?: string;
  /** Additional CSS classes applied to the hub wrapper. */
  className?: string;
  /** Hide the admin edit/data buttons even for authorized users. */
  hideAdminControls?: boolean;
  /** Content tab type filter override (e.g. ['news', 'article']). */
  contentTypes?: string[];
  /** Search placeholder override for the content tab. */
  searchPlaceholder?: string;
  /** Default page limit for tabs (overridden by per-tab context.limit). */
  limit?: number;
  /** Show result count in content tab. Defaults to true. */
  showResultCount?: boolean;
  /** Content tab filter layout: 'desktop' | 'mobile' | 'auto'. */
  filterLayout?: 'desktop' | 'mobile' | 'auto';
  /** Custom empty state for the content tab. */
  emptyState?: {
    title: string;
    description: string;
  };
  /**
   * Tag filter options shown as a toggleable filter in the content tab.
   * Each option has a label and value (single tag or array of tags).
   * Example: [{ "value": "nz", "label": "New Zealand" }, { "value": ["au", "aus"], "label": "Australia" }]
   */
  tagFilter?: HubTagFilterOption[];
  /** Label for the tag filter panel. Defaults to "Tags". */
  tagFilterLabel?: string;
  /** Horizontal alignment of the tag filter row. Defaults to 'left'. */
  tagFilterAlign?: 'left' | 'center' | 'right';
  /** Controls whether tag filters wrap onto multiple lines or scroll horizontally with fade indicators. Defaults to 'wrap'. */
  tagFilterOverflow?: 'wrap' | 'scroll';
  /**
   * Restricts which tabs show the global tag filter row (tagFilter), keyed by
   * tab `id`. When omitted, the tag filter row shows on every tab. Provide a
   * list of tab ids (e.g. ['whatson', 'news']) to only show it while those
   * tabs are active. An empty array hides it on every tab.
   */
  tagFilterTabs?: string[];
}
interface HubData {
  tabs: HubTabData[];
  context?: Record<string, any>;
  tooltipsDisabled?: boolean;
  backLabel?: string;
  /** Tab id to link back to (used to derive backHref) */
  backTab?: string;
  /** When true, shows a Follow button for this content hub */
  followEnabled?: boolean;
  /** JSON-configurable display options */
  options?: HubOptions;
  /** Membership settings – when enabled, allows users to join this hub */
  membership?: {
    enabled: boolean;
    autoApprove: boolean;
    roles?: string[];
    capacity?: number | null;
    joinLabel?: string;
    leaveLabel?: string;
    showMemberCount?: boolean;
    membersListPublic?: boolean;
  };
}
interface HubHeadData {
  title?: string;
  description?: string;
}

// --- Component ---

interface ContentHubViewProps {
  content: ApiContent;
}
export function ContentHubView({
  content
}: ContentHubViewProps) {
  const hubData = (content.data as any)?.hub as HubData | undefined;
  const headData = (content.data as any)?.head as HubHeadData | undefined;
  const options = hubData?.options;
  const slug = content.slug || String(content.id);
  const {
    data: sessionData
  } = useSession();
  const isEntityOwned = content.ownerType === 'entity';
  const entityId = isEntityOwned ? Number(content.ownerId) : 0;
  const {
    hasPermission,
    isPlatformAdmin
  } = useResourcePermissions('entity', entityId);
  const isPlatformAdminUser = !!sessionData?.user?.isAdmin;
  const canEdit = isPlatformAdminUser || isPlatformAdmin || isEntityOwned && hasPermission('edit');
  const [showDataEditor, setShowDataEditor] = useState(false);
  const tabs: HubTab[] = useMemo(() => {
    if (!hubData?.tabs) return [];
    return hubData.tabs.map(tab => {
      const resolved: HubTab = {
        id: tab.id,
        title: tab.title,
        view: tab.view,
        tooltip: tab.tooltip,
        description: tab.description,
        disabled: tab.disabled,
        context: tab.context
      };
      if (tab.icon) {
        const LucideIcon = icons[tab.icon as keyof typeof icons];
        if (LucideIcon) {
          resolved.icon = <LucideIcon className="w-4 h-4" />;
        }
      }
      if (tab.component) {
        const Comp = HUB_COMPONENT_REGISTRY[tab.component];
        if (Comp) {
          resolved.component = () => <div className="max-w-[1000px] mx-auto">
              <Comp />
            </div>;
        }
      }
      return resolved;
    });
  }, [hubData?.tabs]);
  const context = useMemo(() => {
    const base: Record<string, any> = {
      ...(hubData?.context || {}),
      hubId: content.id
    };
    // Merge JSON options into context so tab views can use them
    if (options) {
      if (options.contentTypes) base.contentTypes = options.contentTypes;
      if (options.searchPlaceholder) base.searchPlaceholder = options.searchPlaceholder;
      if (options.limit) base.limit = options.limit;
      if (options.showResultCount !== undefined) base.showResultCount = options.showResultCount;
      if (options.filterLayout) base.filterLayout = options.filterLayout;
      if (options.emptyState) base.emptyState = options.emptyState;
    }
    // Membership context – passed to MembersTab via hub-level context
    if (hubData?.membership?.enabled) {
      base.membershipObjectType = 'content';
      base.membershipObjectId = content.id;
      base.membershipIsAdmin = canEdit;
    }
    return base;
  }, [hubData?.context, hubData?.membership, content.id, options, canEdit]);
  const backHref = hubData?.backTab ? `/content/${slug}?tab=${hubData.backTab}` : undefined;
  const backLabel = hubData?.backLabel;

  // Build follow config when followEnabled is set in hub data
  const followConfig = useMemo(() => {
    if (!hubData?.followEnabled) return undefined;
    return {
      objectId: content.id,
      objectType: 'content' as const,
      following: content.following ?? false
    };
  }, [hubData?.followEnabled, content.id, content.following]);
  const headTitle = headData?.title || `${content.title || 'Hub'} | Sizzle`;
  const headDescription = headData?.description || content.blurb || '';
  return <>
      <Head>
        <title>{headTitle}</title>
        {headDescription && <meta name="description" content={headDescription} />}
      </Head>
      {canEdit && !options?.hideAdminControls && <div className="flex items-center gap-2 justify-end px-4 pt-2">
          <Link href={`/admin/content/${content.id}/edit`} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 cursor-pointer">
            <Edit className="h-3 w-3" />
            Edit
          </Link>
          <button onClick={() => setShowDataEditor(true)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 cursor-pointer">
            <Database className="h-3 w-3" />
            Edit Data
          </button>
        </div>}
      <HubView id={slug} title={options?.hideTitle ? undefined : options?.titleOverride || content.title || undefined} context={context} tabs={tabs} tooltipsDisabled={hubData?.tooltipsDisabled} backLabel={backLabel} backHref={backHref} follow={followConfig} tagFilter={options?.tagFilter} tagFilterLabel={options?.tagFilterLabel} tagFilterAlign={options?.tagFilterAlign} tagFilterOverflow={options?.tagFilterOverflow} tagFilterTabs={options?.tagFilterTabs} />
      {canEdit && <ObjectDataEditorDialog isOpen={showDataEditor} onClose={() => setShowDataEditor(false)} objectType="content" objectId={content.id} />}
    </>;
}